import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { Clock, ArrowRight } from "lucide-react";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data));
  }, []);
  const [featured, ...rest] = posts;

  return (
    <>
      <SEO title="Spark" description="Guides, comparisons and education on selling gift cards." />

      {/* Simple hero */}
      <section className="search-home-hero-section" data-testid="blog-hero">
        <div className="main-container w-container">
          <div className="search-heading" style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
              Spark
            </div>
            <h1 className="h1 _700-bold">Guides from the <span style={{ color: "#346af8" }}>ops team</span></h1>
            <p className="main-body-text _500-medium" style={{ marginTop: 18, color: "#4a4d5f" }}>
              Practical guides on selling gift cards, comparing rates, and getting paid faster — written by the people who actually do it.
            </p>
          </div>
        </div>
      </section>

      {/* Featured post big card */}
      <section className="blog-post-section" style={{ paddingTop: 20 }} data-testid="blog-list">
        <div className="main-container w-container">
          {featured && (
            <Link to={`/blog/${featured.slug}`} data-testid="blog-featured"
              style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 0, background: "#fff", border: "1px solid #eaeaf0", borderRadius: 28, overflow: "hidden", textDecoration: "none", marginBottom: 40, transition: "all .3s" }}
              className="blog-featured">
              {featured.hero_image_url && (
                <div style={{ background: "#f4f4f8", minHeight: 320 }}>
                  <img src={featured.hero_image_url} alt={featured.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} loading="eager" />
                </div>
              )}
              <div style={{ padding: "40px 44px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div style={metaRow}>
                  <span style={{ color: "#346af8", fontWeight: 700 }}>{featured.category}</span>
                  <span style={{ color: "#9a93a6" }}>·</span>
                  <span style={{ color: "#9a93a6", display: "inline-flex", alignItems: "center", gap: 4 }}><Clock style={{ width: 12, height: 12 }} /> {featured.read_time} min</span>
                  {featured.tier === "pillar" && <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 800, letterSpacing: ".08em", padding: "3px 9px", borderRadius: 90, background: "#0b0d17", color: "#fff" }}>PILLAR</span>}
                </div>
                <h2 className="h2 _700-bold" style={{ marginTop: 14, lineHeight: 1.1 }}>{featured.title}</h2>
                <p style={{ marginTop: 14, color: "#4a4d5f", lineHeight: 1.6, fontSize: 15.5 }}>{featured.excerpt}</p>
                {featured.author_name && (
                  <div style={{ marginTop: 18, fontSize: 13, color: "#9a93a6" }}>
                    By <strong style={{ color: "#0b0d17" }}>{featured.author_name}</strong>{featured.author_title && <span> · {featured.author_title}</span>}
                  </div>
                )}
                <span style={{ marginTop: 22, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 15, fontWeight: 700, color: "#346af8" }}>
                  Read article <ArrowRight style={{ width: 16, height: 16 }} />
                </span>
              </div>
            </Link>
          )}

          {/* Rest of the posts as blog-post-block style grid */}
          <div style={{ display: "grid", gap: 24, gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
            {rest.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-post-${p.slug}`} className="blog-post-block"
                style={{ background: "#fff", border: "1px solid #eaeaf0", borderRadius: 22, overflow: "hidden", textDecoration: "none", transition: "all .25s", display: "flex", flexDirection: "column" }}>
                {p.hero_image_url && (
                  <div style={{ aspectRatio: "16/10", overflow: "hidden", background: "#f4f4f8" }}>
                    <img src={p.hero_image_url} alt={p.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
                  </div>
                )}
                <div style={{ padding: 26 }} className="blog-post-block-text-container">
                  <div style={{ ...metaRow, fontSize: 11 }}>
                    <span style={{ color: "#346af8", fontWeight: 700 }}>{p.category}</span>
                    <span style={{ color: "#9a93a6" }}>·</span>
                    <span style={{ color: "#9a93a6", display: "inline-flex", alignItems: "center", gap: 4 }}><Clock style={{ width: 12, height: 12 }} /> {p.read_time} min</span>
                  </div>
                  <h3 style={{ fontFamily: '"Hanken Grotesk", sans-serif', fontSize: 22, fontWeight: 700, color: "#0b0d17", marginTop: 12, lineHeight: 1.25 }}>{p.title}</h3>
                  <p style={{ fontSize: 14.5, color: "#6c6376", marginTop: 10, lineHeight: 1.6 }}>{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>

          {posts.length === 0 && (
            <div style={{ textAlign: "center", padding: 60, color: "#9a93a6" }}>Loading posts…</div>
          )}
        </div>
      </section>

      <style>{`
        .blog-featured:hover { box-shadow: 0 40px 80px -30px rgba(41,45,85,.35); border-color: #346af8; }
        .blog-post-block:hover { transform: translateY(-4px); border-color: #346af8; box-shadow: 0 24px 48px -24px rgba(41,45,85,.3); }
        @media (max-width: 767px) { .blog-featured { grid-template-columns: 1fr !important; } .blog-featured > div:last-child { padding: 30px !important; } }
      `}</style>
    </>
  );
}

const metaRow = { display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" };
